import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";

const AppContext = createContext(null);

const POLL_ACTIVE = 10000;
const POLL_IDLE   = 30000;
const JOBS_TTL    = 60000;
const LISTS_TTL   = 5 * 60 * 1000;

export function AppProvider({ children }) {
  const [cmsList, setCmsList] = useState([]);
  const [cmsMajorList, setCmsMajorList] = useState([]);
  const [cmsFoundInData, setCmsFoundInData] = useState({});
  const [branchList, setBranchList] = useState([]);
  const [techList, setTechList] = useState({});
  const [stats, setStats] = useState(null);
  const [crawlStatus, setCrawlStatus] = useState(null);
  const [researchStatus, setResearchStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(Date.now());
  const [currentPollMs, setCurrentPollMs] = useState(POLL_IDLE);

  const intervalRef    = useRef(null);
  const pollMsRef      = useRef(POLL_IDLE);
  const jobsFetchedAt  = useRef(0);
  const listsFetchedAt = useRef(0);

  const loadLists = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - listsFetchedAt.current < LISTS_TTL) return;
    try {
      const [cmsRes, branchRes, techRes] = await Promise.all([
        api.get("/cms-list"),
        api.get("/branch-list"),
        api.get("/tech-list"),
      ]);
      const foundInData = cmsRes.data.found_in_data || {};
      const sorted = Object.keys(foundInData)
        .filter(c => c && c !== "unknown" && foundInData[c] > 0)
        .sort((a, b) => (foundInData[b] || 0) - (foundInData[a] || 0));
      setCmsList(sorted);
      setCmsMajorList(Object.keys(cmsRes.data.major_versions || {}));
      setCmsFoundInData(foundInData);
      setBranchList(branchRes.data.all_branches || []);
      setTechList(techRes.data || {});
      setListsLoaded(true);
      listsFetchedAt.current = now;
    } catch {
      setListsLoaded(true);
    }
  }, []);

  const loadJobs = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - jobsFetchedAt.current < JOBS_TTL) return;
    try {
      const res = await api.get("/jobs");
      setJobs(res.data.jobs || []);
      jobsFetchedAt.current = now;
    } catch {}
  }, []);

  const scheduleNext = useCallback((ms) => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      pollRef.current();
    }, ms);
  }, []);

  // Use a ref so the interval closure always calls the latest pollLive
  const pollRef = useRef(null);

  const pollLive = useCallback(async () => {
    try {
      const [statsRes, crawlRes, researchRes] = await Promise.all([
        api.get("/stats"),
        api.get("/crawl/status"),
        api.get("/research/status"),
      ]);
      setStats(statsRes.data);
      setCrawlStatus(crawlRes.data);
      setResearchStatus(researchRes.data);
      setLastRefreshedAt(Date.now());

      const nowActive = !!(crawlRes.data?.active || researchRes.data?.active);
      const newPollMs = nowActive ? POLL_ACTIVE : POLL_IDLE;

      if (crawlRes.data?.active) loadLists();
      if (researchRes.data?.active === false) loadJobs();

      // Always re-sync interval if the desired interval differs from current
      if (newPollMs !== pollMsRef.current) {
        pollMsRef.current = newPollMs;
        setCurrentPollMs(newPollMs);
        scheduleNext(newPollMs);
      }
    } catch {}
  }, [loadLists, loadJobs, scheduleNext]);

  // Keep ref in sync so stale interval closure always calls latest version
  useEffect(() => {
    pollRef.current = pollLive;
  }, [pollLive]);

  useEffect(() => {
    api.get("/stats/cached").then(res => setStats(res.data)).catch(() => {});
    loadLists(true);
    loadJobs(true);
    pollLive();
    // Start with POLL_IDLE; pollLive will immediately switch to POLL_ACTIVE if needed
    scheduleNext(POLL_IDLE);
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshNow = useCallback(() => {
    pollLive();
    loadJobs(true);
  }, [pollLive, loadJobs]);

  const crawlerActive  = !!(crawlStatus?.active);
  const researchActive = !!(researchStatus?.active);

  return (
    <AppContext.Provider value={{
      cmsList, cmsMajorList, cmsFoundInData, branchList, techList,
      stats, crawlStatus, researchStatus,
      crawlerActive, researchActive,
      jobs, loadJobs,
      listsLoaded, refreshNow,
      lastRefreshedAt, currentPollMs,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used inside AppProvider");
  return ctx;
};

export { POLL_ACTIVE, POLL_IDLE };
